import { create } from 'zustand'
import type { Vaccine, VaccineCreate, VaccineUpdate } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface VaccinesState {
  vaccines: Vaccine[]
  upcomingVaccines: Vaccine[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchVaccinesByPet: (petId: string) => Promise<void>
  fetchUpcomingVaccines: () => Promise<void>
  createVaccine: (petId: string, data: VaccineCreate) => Promise<Vaccine>
  updateVaccine: (id: string, data: VaccineUpdate) => Promise<Vaccine>
  deleteVaccine: (id: string) => Promise<void>
  clearError: () => void
  clearVaccines: () => void
}

export const useVaccinesStore = create<VaccinesState>((set) => ({
  vaccines: [],
  upcomingVaccines: [],
  isLoading: false,
  error: null,

  fetchVaccinesByPet: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ vaccines: Vaccine[] }>(`/api/pets/${petId}/vaccines`)
      set({ vaccines: response.vaccines, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar vacunas'
      set({ error: message, isLoading: false })
    }
  },

  fetchUpcomingVaccines: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ vaccines: Vaccine[] }>('/api/vaccines/upcoming')
      set({ upcomingVaccines: response.vaccines, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar próximas vacunas'
      set({ error: message, isLoading: false })
    }
  },

  createVaccine: async (petId: string, data: VaccineCreate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ vaccine: Vaccine }>(`/api/pets/${petId}/vaccines`, data)
      set((state) => ({
        vaccines: [response.vaccine, ...state.vaccines],
        isLoading: false,
      }))
      return response.vaccine
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al registrar vacuna'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateVaccine: async (id: string, data: VaccineUpdate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ vaccine: Vaccine }>(`/api/vaccines/${id}`, data)
      set((state) => ({
        vaccines: state.vaccines.map((v) => (v.id === id ? response.vaccine : v)),
        upcomingVaccines: state.upcomingVaccines.map((v) => (v.id === id ? response.vaccine : v)),
        isLoading: false,
      }))
      return response.vaccine
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar vacuna'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteVaccine: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/vaccines/${id}`)
      set((state) => ({
        vaccines: state.vaccines.filter((v) => v.id !== id),
        upcomingVaccines: state.upcomingVaccines.filter((v) => v.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar vacuna'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  clearError: () => set({ error: null }),
  clearVaccines: () => set({ vaccines: [], upcomingVaccines: [] }),
}))
